/**
 * 桌面通知工具类
 * 用于在PC端显示桌面通知
 */
import store from '@/store';
import { resolveMediaUrl } from './mediaUrl';

class NotificationManager {
	constructor() {
		this.isSupported = 'Notification' in window;
		this.permission = this.isSupported ? Notification.permission : 'denied';
		this.notifications = new Map(); // 存储通知实例，避免重复
		this.maxNotifications = 5; // 最大同时显示的通知数量
		this.lastNotificationTime = 0; // 上次显示通知的时间
		this.notificationCooldown = 2000; // 通知冷却时间（毫秒）
		// 项目 logo 路径，用于桌面通知图标和徽章
		this.logoPath = '/logo.png'; // public 目录下的 logo
	}

	/**
	 * 请求通知权限
	 */
	async requestPermission() {
		if (!this.isSupported) {
			console.warn('浏览器不支持桌面通知');
			return false;
		}

		if (this.permission === 'default') {
			this.permission = await Notification.requestPermission();
		}

		return this.permission === 'granted';
	}

	/**
	 * 检查是否有通知权限
	 */
	hasPermission() {
		return this.permission === 'granted';
	}

	/**
	 * 检查是否应该显示通知
	 * 只有在页面不可见或浏览器最小化时才显示通知
	 */
	shouldShowNotification() {
		// 如果页面处于活动状态（可见、未最小化、有焦点），则不显示通知
		const shouldShow = !this.isPageActive();
		
		// 添加调试信息
		if (process.env.NODE_ENV === 'development') {
			console.log('通知显示检查:', {
				shouldShow,
				visibilityState: document.visibilityState,
				windowSize: `${window.innerWidth}x${window.innerHeight}`,
				hasFocus: document.hasFocus(),
				isActive: this.isPageActive()
			});
		}
		
		return shouldShow;
	}

	/**
	 * 更精确的页面状态检测
	 */
	isPageActive() {
		// 检查页面是否可见
		if (document.visibilityState !== 'visible') {
			return false;
		}

		// 检查窗口是否最小化
		if (window.innerHeight <= 0 || window.innerWidth <= 0) {
			return false;
		}

		// 检查是否有焦点
		if (!document.hasFocus()) {
			return false;
		}

		// 检查是否在iframe中（某些情况下iframe可能影响焦点检测）
		if (window !== window.top) {
			return false;
		}

		return true;
	}

	/**
	 * 获取当前页面状态信息
	 */
	getPageStatus() {
		return {
			visibilityState: document.visibilityState,
			windowSize: {
				width: window.innerWidth,
				height: window.innerHeight
			},
			hasFocus: document.hasFocus(),
			isActive: this.isPageActive()
		};
	}

	/**
	 * 显示桌面通知
	 * @param {Object} options 通知选项
	 * @param {string} options.title 通知标题
	 * @param {string} options.body 通知内容
	 * @param {string} options.icon 通知图标
	 * @param {string} options.tag 通知标签，用于去重
	 * @param {Function} options.onClick 点击回调
	 * @param {Function} options.onClose 关闭回调
	 */
	show(options) {
		if (!this.hasPermission()) {
			console.warn('没有通知权限');
			return null;
		}

		// 检查是否应该显示通知
		if (!this.shouldShowNotification()) {
			console.log('页面处于活动状态，跳过桌面通知');
			return null;
		}
		// 延迟检查，确保页面状态稳定
		setTimeout(() => {
			if (!this.shouldShowNotification()) {
				console.log('延迟检查：页面仍处于活动状态，跳过桌面通知');
				return null;
			}
		}, 100);

		// 检查通知冷却时间
		const now = Date.now();
		if (now - this.lastNotificationTime < this.notificationCooldown) {
			console.log('通知冷却中，跳过桌面通知');
			return null;
		}

		// 清理过多的通知
		this.cleanupNotifications();

		// 创建通知
		const notification = new Notification(options.title, {
			body: options.body,
			icon: options.icon || this.logoPath, // 使用项目 logo 作为默认图标
			tag: options.tag, // 相同tag的通知会替换
			requireInteraction: false,
			silent: false,
			badge: this.logoPath, // 使用项目 logo 作为徽章
			image: options.image,
			actions: options.actions || []
		});

		// 设置点击事件
		if (options.onClick) {
			notification.onclick = (event) => {
				event.preventDefault();
				options.onClick(event);
				notification.close();
			};
		}

		// 设置关闭事件
		if (options.onClose) {
			notification.onclose = options.onClose;
		}

		// 自动关闭通知（5秒后）
		setTimeout(() => {
			if (notification) {
				notification.close();
			}
		}, 5000);

		// 存储通知实例
		if (options.tag) {
			this.notifications.set(options.tag, notification);
		}

		// 更新最后通知时间
		this.lastNotificationTime = Date.now();

		return notification;
	}

	/**
	 * 清理过多的通知
	 */
	cleanupNotifications() {
		if (this.notifications.size >= this.maxNotifications) {
			// 关闭最早的通知
			const firstKey = this.notifications.keys().next().value;
			const firstNotification = this.notifications.get(firstKey);
			if (firstNotification) {
				firstNotification.close();
			}
			this.notifications.delete(firstKey);
		}
	}

	/**
	 * 关闭指定通知
	 * @param {string} tag 通知标签
	 */
	close(tag) {
		const notification = this.notifications.get(tag);
		if (notification) {
			notification.close();
			this.notifications.delete(tag);
		}
	}

	/**
	 * 关闭所有通知
	 */
	closeAll() {
		this.notifications.forEach(notification => {
			notification.close();
		});
		this.notifications.clear();
	}

	resolveNotifyIcon(url) {
		if (!url) {
			return this.logoPath;
		}
		const resolved = resolveMediaUrl(url, store.state.configStore.s3Url);
		if (resolved && (/^https?:\/\//i.test(resolved) || resolved.startsWith('data:') || resolved.startsWith('blob:'))) {
			return resolved;
		}
		return this.logoPath;
	}

	/**
	 * 强制显示通知（忽略页面状态检查）
	 * 用于特殊情况，如重要系统通知
	 */
	forceShow(options) {
		if (!this.hasPermission()) {
			console.warn('没有通知权限');
			return null;
		}

		// 清理过多的通知
		this.cleanupNotifications();

		// 创建通知
		const notification = new Notification(options.title, {
			body: options.body,
			icon: options.icon || this.logoPath, // 使用项目 logo 作为默认图标
			tag: options.tag,
			requireInteraction: false,
			silent: false,
			badge: this.logoPath, // 使用项目 logo 作为徽章
			image: options.image,
			actions: options.actions || []
		});

		// 设置点击事件
		if (options.onClick) {
			notification.onclick = (event) => {
				event.preventDefault();
				options.onClick(event);
				notification.close();
			};
		}

		// 设置关闭事件
		if (options.onClose) {
			notification.onclose = options.onClose;
		}

		// 自动关闭通知（5秒后）
		setTimeout(() => {
			if (notification) {
				notification.close();
			}
		}, 5000);

		// 存储通知实例
		if (options.tag) {
			this.notifications.set(options.tag, notification);
		}

		// 更新最后通知时间
		this.lastNotificationTime = Date.now();

		return notification;
	}

	/**
	 * 显示私聊消息通知
	 * @param {Object} message 消息对象
	 * @param {Object} friend 好友信息
	 */
	showPrivateMessageNotification(message, friend) {
		const content = this.getMessageContent(message);
		const tag = `private_${friend.id}_${message.id}`;

		this.show({
			title: friend.showNickName || '未知用户',
			body: content,
			icon: this.resolveNotifyIcon(friend.headImage),
			tag: tag,
			onClick: () => {
				// 点击通知时聚焦到对应聊天窗口
				window.focus();
				// 触发事件，让应用切换到对应聊天
				window.dispatchEvent(new CustomEvent('focusChat', {
					detail: {
						type: 'PRIVATE',
						targetId: friend.id
					}
				}));
			}
		});
	}

	/**
	 * 显示群聊消息通知
	 * @param {Object} message 消息对象
	 * @param {Object} group 群组信息
	 */
	showGroupMessageNotification(message, group) {
		const content = this.getMessageContent(message);
		const tag = `group_${group.id}_${message.id}`;

		this.show({
			title: `${group.showGroupName || '未知群聊'} - ${message.sendNickName || '未知用户'}`,
			body: content,
			icon: this.resolveNotifyIcon(group.headImage),
			tag: tag,
			onClick: () => {
				// 点击通知时聚焦到对应聊天窗口
				window.focus();
				// 触发事件，让应用切换到对应聊天
				window.dispatchEvent(new CustomEvent('focusChat', {
					detail: {
						type: 'GROUP',
						targetId: group.id
					}
				}));
			}
		});
	}

	/**
	 * 显示系统消息通知
	 * @param {Object} message 消息对象
	 */
	showSystemMessageNotification(message) {
		const content = this.getMessageContent(message);
		const tag = `system_${message.seqNo}`;

		this.show({
			title: '系统通知',
			body: content,
			icon: this.logoPath,
			tag: tag,
			onClick: () => {
				// 点击通知时聚焦到系统消息
				window.focus();
				window.dispatchEvent(new CustomEvent('focusChat', {
					detail: {
						type: 'SYSTEM',
						targetId: 0
					}
				}));
			}
		});
	}

	/**
	 * 显示好友申请通知
	 * @param {Object} request 好友申请信息
	 */
	showFriendRequestNotification(request) {
		this.show({
			title: '新的好友申请',
			body: `${request.sendNickName || '未知用户'} 请求添加您为好友`,
			icon: this.resolveNotifyIcon(request.sendHeadImage),
			tag: `friend_request_${request.id}`,
			onClick: () => {
				// 点击通知时跳转到好友页面
				window.focus();
				window.dispatchEvent(new CustomEvent('navigateTo', {
					detail: { path: '/home/friend' }
				}));
			}
		});
	}

	/**
	 * 显示入群申请通知（管理员）
	 * @param {Object} request 入群申请信息
	 */
	showGroupJoinRequestNotification(request) {
		this.show({
			title: '新的入群申请',
			body: `${request.sendNickName || '未知用户'} 申请加入群聊${request.groupName ? `：${request.groupName}` : ''}`,
			icon: this.resolveNotifyIcon(request.sendHeadImage),
			tag: `group_join_request_${request.id || Date.now()}`,
			onClick: () => {
				window.focus();
				window.dispatchEvent(new CustomEvent('navigateTo', {
					detail: { path: '/home/group' }
				}));
			}
		});
	}

	/**
	 * 显示入群申请被拒绝通知（申请人）
	 * @param {Object} request 入群申请信息
	 */
	showGroupJoinRequestRejectNotification(request) {
		this.show({
			title: '入群申请结果',
			body: `您申请加入的群聊${request.groupName ? `「${request.groupName}」` : ''}已被拒绝`,
			icon: this.resolveNotifyIcon(request.groupHeadImage || request.headImage),
			tag: `group_join_request_reject_${request.id || Date.now()}`,
			onClick: () => {
				window.focus();
				window.dispatchEvent(new CustomEvent('navigateTo', {
					detail: { path: '/home/group' }
				}));
			}
		});
	}

	/**
	 * 显示通话通知
	 * @param {Object} callInfo 通话信息
	 * @param {string} type 通话类型 'voice' | 'video'
	 */
	showCallNotification(callInfo, type) {
		const callType = type === 'voice' ? '语音通话' : '视频通话';
		const tag = `call_${callInfo.sendId}_${Date.now()}`;

		this.show({
			title: `${callInfo.sendNickName || '未知用户'} 的${callType}`,
			body: `点击接听或拒绝`,
			icon: this.resolveNotifyIcon(callInfo.sendHeadImage),
			tag: tag,
			actions: [
				{
					action: 'accept',
					title: '接听'
				},
				{
					action: 'reject',
					title: '拒绝'
				}
			],
			onClick: (event) => {
				// 处理通知点击事件
				if (event.action === 'accept') {
					window.dispatchEvent(new CustomEvent('acceptCall', {
						detail: { callInfo, type }
					}));
				} else if (event.action === 'reject') {
					window.dispatchEvent(new CustomEvent('rejectCall', {
						detail: { callInfo, type }
					}));
				}
			}
		});
	}

	/**
	 * 获取消息内容
	 * @param {Object} message 消息对象
	 * @returns {string} 消息内容
	 */
	getMessageContent(message) {
		switch (message.type) {
			case 0: // TEXT
				return message.content || '[文本消息]';
			case 1: // IMAGE
				return '[图片]';
			case 2: // FILE
				return '[文件]';
			case 3: // AUDIO
				return '[语音]';
			case 4: // VIDEO
				return '[视频]';
			case 5: // USER_CARD
				return '[个人名片]';
			case 6: // GROUP_CARD
				return '[群名片]';
			case 40: // ACT_RT_VOICE
				return '[语音通话]';
			case 41: // ACT_RT_VIDEO
				return '[视频通话]';
			case 53: // SYSTEM_MESSAGE
				return message.title || message.content || '[系统消息]';
			default:
				return message.content || '[未知消息类型]';
		}
	}
}

// 创建单例实例
const notificationManager = new NotificationManager();

export default notificationManager; 